import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { VgApiService, VgCoreModule } from '@videogular/ngx-videogular/core';
import { VgControlsModule, VgFullscreenComponent } from '@videogular/ngx-videogular/controls';
import { VgOverlayPlayModule } from '@videogular/ngx-videogular/overlay-play';
import { VgBufferingModule } from '@videogular/ngx-videogular/buffering';

import { VideoSearchResult } from '../../model/video-search-result.model';
import { SceneSelectorComponent } from './addons/scene-selector/scene-selector.component';
import { QualityControlSelectorComponent } from './addons/quality-control-selector/quality-control-selector.component';
import { Scene } from '../../model/scene';
import { VideoSource } from '../../model/video-source';
import { VideoMetadataComponent } from '../video-metadata/video-metadata.component';

export enum VideoPlayerEvents {
  PLAY = 'play',
  PAUSE = 'pause',
  SEEK = 'seek',
  HOVER_IN = 'mouseenter',
  HOVER_OUT = 'mouseleave',
}

export interface VideoPlayerSubscriber {
  notify(event: VideoPlayerEvents): void;
}

@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    VgCoreModule,
    VgControlsModule,
    VgOverlayPlayModule,
    VgBufferingModule,
    SceneSelectorComponent,
    QualityControlSelectorComponent,
    VideoMetadataComponent
  ]
})
export class VideoPlayerComponent implements OnInit, OnDestroy {


  @Input() video: VideoSearchResult;
  @Output() onSceneSelected = new EventEmitter<Scene>();
  @Output() onScenesLoaded = new EventEmitter<void>();

  @ViewChild(VgFullscreenComponent) vgFullScreenComponent!: VgFullscreenComponent;

  api: VgApiService = new VgApiService();

  selectedVideoSource: VideoSource = null;
  vgOverlayPlay: HTMLElement | null = null;

  isMouseHovering = false;
  hoverStatus: VideoPlayerEvents.HOVER_IN | VideoPlayerEvents.HOVER_OUT | null = null;

  private isPlaying = false;
  private suscribers: VideoPlayerSubscriber[] = [];

  constructor(
    private elementRef: ElementRef
  ) { }

  ngOnInit() {
    this.selectedVideoSource = this.video.media_urls[0];
    this.vgOverlayPlay = this.elementRef.nativeElement.querySelector('#vg-player');
  }

  ngOnDestroy() {
    this.suscribers.length = 0;
    this.suscribers = [];
  }

  onPlayerReady(source: VgApiService) {
    this.api = source;
    this.api.getDefaultMedia().subscriptions.canPlay.subscribe(() => {
    });


    // Prevent the video from playing when the user loads the page
    this.api.pause();

    this.api.subscriptions.playing.subscribe((val: any) => {
      if (val) {
        this.isPlaying = true;
        this.notifySubscribers(VideoPlayerEvents.PLAY);
      }
    });

    this.api.subscriptions.pause.subscribe((val: any) => {
      if (val) {
        this.isPlaying = false;
        this.notifySubscribers(VideoPlayerEvents.PAUSE);
      }
    });

    // Notify subscribers every 100ms
    setInterval(() => {
      if (this.api) {
        this.notifySubscribers(VideoPlayerEvents.SEEK);
      }
    }, 100);

  }

  public isFullScreen(): boolean {
    return this.vgFullScreenComponent?.isFullscreen || false;
  }

  public getVideoSources(): VideoSource[] {
    return this.video.media_urls
  }

  public setVideoSource(videoSource: VideoSource) {
    const time_in_seconds = this.getCurrentTime();
    const wasPlaying = this.isPlaying;

    this.selectedVideoSource = videoSource;
    setTimeout(() => {
      this.seekVideoTo(time_in_seconds);
      setTimeout(() => {
        if (wasPlaying) {
          this.api.play();
        } else {
          this.api.pause();
        }
      }, 10);
    });
  }

  public getCurrentTime(): number {
    if (this.api) {
      return this.api.getDefaultMedia().currentTime;
    }
    return 0;
  }

  public seekVideoTo(time_in_seconds: number): void {
    if (this.api) {
      this.api.getDefaultMedia().currentTime = time_in_seconds;
    }
  }

  public showVideoOverlay(): boolean {
    return this.isMouseHovering || !this.isPlaying;
  }

  public subscribeToVideoEvents(subscription: VideoPlayerSubscriber): void {
    this.suscribers.push(subscription);
  }

  public sceneSelected(scene: Scene): void {
    this.onSceneSelected.emit(scene);
  }

  scenesLoaded() {
    this.onScenesLoaded.emit();
  }

  @HostListener('mouseenter')
  public mouseenterListener(): void {
    this.isMouseHovering = true;
    this.hoverStatus = VideoPlayerEvents.HOVER_IN;
    this.notifySubscribers(VideoPlayerEvents.HOVER_IN);
  }

  @HostListener('mouseleave')
  public mouseleaveListener(): void {
    const delay_in_seconds = 2;
    this.hoverStatus = VideoPlayerEvents.HOVER_OUT;
    setTimeout(() => {
      this.notifySubscribers(VideoPlayerEvents.HOVER_OUT);


      if (this.hoverStatus !== "mouseleave") {
        return;
      }

      this.isMouseHovering = false;
      this.hoverStatus = VideoPlayerEvents.HOVER_OUT;
    }, delay_in_seconds * 1000);
  }

  private notifySubscribers(event: VideoPlayerEvents): void {
    this.suscribers.forEach((subscription) => {
      subscription.notify(event);
    });
  }

}
